import { Button, ButtonsGroup, Modal, Text, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { getOrganizationViewUrl } from '../../../../utils/routing/getOrganizationViewUrl'
import type { RelatedInvoice } from '../../types/relatedInvoice'
import { getRelatedInvoiceRoute } from '../../utils/depositInvoices/getRelatedInvoiceRoute'
import { modalVariantToTranslationKeys } from './constants/modalVariantToTranslationKeys'
import { BlockCancelInvoiceActionVariant } from './enums/blockCancelInvoiceActionVariant'
import { getBlockCancelInvoiceActionModalId } from './utils/getBlockCancelInvoiceActionModalId'

interface BlockCancelInvoiceActionModalProps {
  invoiceId: string
  relatedFinalInvoice: RelatedInvoice
  variant: BlockCancelInvoiceActionVariant
}

export const BlockCancelInvoiceActionModal = ({
  invoiceId,
  relatedFinalInvoice,
  variant,
}: BlockCancelInvoiceActionModalProps): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { organization } = useUserOrganization()

  const modalId = getBlockCancelInvoiceActionModalId(invoiceId, variant)
  const { close } = useModal(modalId)
  const { description, title } = modalVariantToTranslationKeys[variant]

  const handleGoToFinalInvoiceButtonClick = useCallback(() => {
    const finalInvoiceRoute = getRelatedInvoiceRoute(relatedFinalInvoice)

    close()
    navigate(getOrganizationViewUrl(organization?.url, finalInvoiceRoute))
  }, [close, navigate, organization?.url, relatedFinalInvoice])

  const handleCloseButtonClick = useCallback(() => close(), [close])

  return (
    <Modal id={modalId} size="s">
      <Modal.Header title={t(title)} />
      <Modal.Body>
        <Text>{t(description)}</Text>
      </Modal.Body>
      <Modal.Footer>
        <ButtonsGroup>
          <Button onClick={handleCloseButtonClick} variant="text">
            {t('close')}
          </Button>
          <Button onClick={handleGoToFinalInvoiceButtonClick}>
            {t('external_invoices.view.cancel_action_blocked_modal.go_to_final_invoice')}
          </Button>
        </ButtonsGroup>
      </Modal.Footer>
    </Modal>
  )
}
